import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EmptyState, FleetPage, FleetSection, Notice, RollupTiles } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatCount, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { zoneName } from '../../../../shared/date/local-time'
import { exportCsv } from '../../../../shared/export/utils'
import { ALERT_STATUS, WordPill } from '../alert-routing/run-words'
import { getGroup, getOccurrences, getBuckets, getBreakdown, setStatus, setLinks, setNote, listGroupAlerts } from './errors-api'
import { useErrorRoles, READ_ONLY } from './use-error-roles'
import { RANGES, STATUS_LABEL, STATUS_TONE, fromLine, groupTitle, isGenericType, pickRange, recurredText, sharePercent, splitPath, statusLine, windowed } from './errors-format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useCopy, manualCopyHint } from '../../../../shared/clipboard/clipboard'
import { triageText } from './triage-copy'
import '../../../../shared/audit/insights/insights.scss'
import './errors.scss'

// ECharts stays out of the main chunk (shared/audit/insights/lazy-load.test.js):
// the chart loads on first use.
const ErrorsChart = React.lazy(() => import('./errors-chart').then(m => ({ default: m.ErrorsChart })))

const MAX_NOTE = 4000
const MAX_LINKS = 10
export const isHttp = u => { try { const x = new URL(u); return x.protocol === 'http:' || x.protocol === 'https:' } catch (e) { return false } }
const ROUTES = '/admin/alert-routing/routes?category=errors'
// A short list shows its newest few; "Show all" opens the rest in place.
export const FIRST = 5
const PAGE = 50

// A long path, cut in the middle: the directories shrink to an ellipsis and
// the file (or function) after the last "/" always shows. The full text is
// the title, and is what a copy picks up.
export const MiddlePath = ({ value }) => {
  const [head, tail] = splitPath(value)
  return (
    <span className="er-path sw-mono" title={value}>
      {head && <span className="er-path-head">{head}</span>}
      <span className="er-path-tail">{tail}</span>
    </span>
  )
}

// A sample's request (opens the request log at the time it happened, when
// the viewer may read it), its job run, or boot.
const OccurrenceLink = ({ o, canOpenRequest }) => (
  o.requestID
    ? (canOpenRequest
        ? <Link to={`/admin/request-log/${o.requestID}?at=${encodeURIComponent(o.occurredAt)}`} aria-label="Open request">
            <span className="er-long" aria-hidden="true">Open request</span><span className="er-short" aria-hidden="true">Request</span>
          </Link>
        : <span className="sw-hint">request {o.requestID.slice(0, 8)}</span>)
    : o.jobRunID ? <span>Job run {o.jobRunID}</span> : <span className="sw-hint">boot</span>
)

// A sample's message, and a failed job's error after it (on the page and in the CSV).
export const occurrenceText = o => [o.message, o.jobError].filter(Boolean).join(' — ')

// One sample as a dense row: when, its status, where it came from (the
// app of its request, its job, or start-up), the host (and the service,
// only when it isn't the error's own), then the request and the stack as
// quiet actions. The message shows only when it isn't the error's message
// (already at the top of the page); a failed job's error always does.
const OccurrenceRow = ({ o, g, canOpenRequest, open, onToggle }) => {
  const message = o.message && o.message !== g.sampleMessage ? o.message : null
  const where = [o.service && o.service !== g.service ? o.service : null, o.host].filter(Boolean).join(' · ')
  const from = fromLine(o.from)
  const stackID = `er-stack-${o.id}`
  return (
    <li className="er-row">
      <div className="er-row-main">
        <span className="er-row-when" title={formatDateTime(o.occurredAt)}>{formatAgo(o.occurredAt)}</span>
        {o.status ? <Pill tone={o.status >= 500 ? 'bad' : 'warn'}>{String(o.status)}</Pill> : null}
        {from && <span className={`er-row-from${o.from.kind === 'unknown' ? ' sw-hint' : ''}`} title={from}>{from}</span>}
        {where && <span className="er-row-where" title={where}>{where}</span>}
      </div>
      <div className="er-row-actions">
        <OccurrenceLink o={o} canOpenRequest={canOpenRequest} />
        {(o.stack || o.componentStack) && <button type="button" className="btn btn-link btn-sm er-quiet" aria-expanded={open} aria-controls={stackID} onClick={onToggle}>Stack</button>}
      </div>
      {message && <div className="er-row-msg sw-mono">{message}</div>}
      {o.jobError && <div className="er-row-msg er-job-error">Job error: {o.jobError}</div>}
      {(o.pagePath || o.failedRequestID) &&
        <div className="er-row-msg" data-testid="client-sample">
          {o.pagePath && <>On page <span className="sw-mono">{o.pagePath}</span></>}
          {o.pagePath && o.failedRequestID && ' · '}
          {o.failedRequestID && <>After a failed request{' '}
            {canOpenRequest
              ? <Link to={`/admin/request-log/${o.failedRequestID}?at=${encodeURIComponent(o.occurredAt)}`}>{o.failedRequestID.slice(0, 8)}</Link>
              : <span className="sw-mono">{o.failedRequestID.slice(0, 8)}</span>}</>}
        </div>}
      {o.stack && open && <pre id={stackID} className="er-code er-row-stack">{o.stack}</pre>}
      {o.componentStack && open && <pre className="er-code er-row-stack" aria-label="Component stack">{o.componentStack}</pre>}
    </li>
  )
}

// "Copy for triage" in the page header: the error as one Markdown block for
// an engineer or an agent (triage-copy.js). The copy starts in the click (what
// Safari needs); when the browser refuses it, the text shows selected, with
// the shortcut to press, until it is closed.
export const TriageCopy = ({ g, latest }) => {
  const copy = useCopy()
  const [manualText, setManualText] = useState(null)
  const field = useRef(null)
  useEffect(() => {
    if (manualText && field.current) { field.current.focus({ preventScroll: true }); field.current.select() }
  }, [manualText])
  const onCopy = () => {
    const text = triageText({ g, latest, origin: window.location.origin, host: window.location.hostname })
    setManualText(null)
    copy.copy(text).then(copied => { if (!copied) setManualText(text) })
  }
  return (
    <div className="er-triage">
      <button type="button" className="btn sw-btn" onClick={onCopy} title="Copies the error as Markdown to paste to an engineer or an agent: no names, emails or tokens">
        {copy.copied ? 'Copied' : 'Copy for triage'}
      </button>
      <span className="visually-hidden" role="status">{copy.copied ? 'Copied for triage' : ''}</span>
      {manualText &&
        <div className="er-triage-manual">
          <label className="sw-label" htmlFor="er-triage-text">{`Couldn't copy. ${manualCopyHint()}.`}</label>
          <textarea id="er-triage-text" ref={field} className="form-control sw-mono" rows={8} readOnly value={manualText} onFocus={e => e.target.select()} />
          <button type="button" className="btn btn-link btn-sm er-quiet" onClick={() => setManualText(null)}>Close</button>
        </div>}
    </div>
  )
}

// The footer of a short list: "Show all N" while only the newest few show,
// then "Show older" while the server has more, and "Show fewer" to fold it.
const ListFoot = ({ total, all, onAll, more, onMore, extra }) => {
  if (total <= FIRST && !more && !extra) return null
  return (
    <div className="er-list-foot">
      {!all && (total > FIRST || more) &&
        <button type="button" className="btn btn-sm sw-btn" onClick={onAll}>{more ? 'Show more' : `Show all ${formatCount(total)}`}</button>}
      {all && more && <button type="button" className="btn btn-sm sw-btn" onClick={onMore}>Show older</button>}
      {all && total > FIRST && <button type="button" className="btn btn-link btn-sm er-quiet" onClick={onAll}>Show newest {FIRST}</button>}
      {extra}
    </div>
  )
}

// The definition list of the request detail drawer: rows with no value are left out.
const KV = ({ rows }) =>
  <dl className="sw-dl er-dl">
    {rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== false).map(([k, v]) =>
      <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
  </dl>

const whereValue = g => (g.source === 'request'
  ? <span className="sw-mono">{g.method} {g.route}</span>
  : <span>{g.source === 'job' ? <>Job <span className="sw-mono">{g.jobName}</span></> : g.source === 'client' ? 'Browser' : 'Boot'}</span>)

// The window picker: the Insights panels' mini toggle (.au-minis).
const RangePicker = ({ value, onChange }) =>
  <div className="au-minis er-range" role="group" aria-label="Chart window">
    {RANGES.map(r =>
      <button key={r.key} type="button" aria-pressed={r.key === value} aria-label={r.label} onClick={() => onChange(r.key)}>
        <span className="er-long" aria-hidden="true">{r.label}</span><span className="er-short" aria-hidden="true">{r.short}</span>
      </button>)}
  </div>

// The Activity section: the events per step over the chosen window, or a
// sentence when there were none in it.
const Activity = ({ g, rangeKey, onRange, buckets, bucketsError, bucketsFailure, now }) => {
  const range = RANGES.find(r => r.key === rangeKey) || RANGES[RANGES.length - 1]
  const points = useMemo(() => (buckets ? windowed(buckets, range, now) : []), [buckets, range, now])
  const total = points.reduce((n, [, c]) => n + c, 0)
  const longer = RANGES.find(r => r.hours > range.hours && Date.parse(g.lastSeenAt) >= now - r.hours * 3600e3)
  return (
    <FleetSection id="er-activity" title="Activity" actions={<RangePicker value={range.key} onChange={onRange} />}>
      {bucketsError && <p className="text-danger small" role="alert">{bucketsError}</p>}
      {bucketsError && <ErrorReference error={bucketsFailure} className="mb-2" />}
      {!buckets && !bucketsError && <div className="er-chart-box" aria-hidden />}
      {buckets && total === 0 &&
        <EmptyState action={longer && <button type="button" className="btn btn-sm sw-btn" onClick={() => onRange(longer.key)}>Show {longer.label}</button>}>
          {`No events in the last ${range.label}. It was last seen ${formatAgo(g.lastSeenAt, now)}.`}
        </EmptyState>}
      {buckets && total > 0 &&
        <>
          <p className="er-chart-caption">
            <b>{plural(total, 'event')}</b> in the last {range.label}
            <span className="sw-hint"> · {range.step === '5m' ? 'per 5 minutes' : range.step === '1h' ? 'per hour' : 'per day'}, times in {zoneName()}</span>
          </p>
          <Suspense fallback={<div className="er-chart-box" aria-hidden />}>
            <ErrorsChart points={points} step={range.step} from={now - range.hours * 3600e3} to={now} label={`Events per ${range.step === '1d' ? 'day' : range.step === '1h' ? 'hour' : '5 minutes'}, last ${range.label}`} />
          </Suspense>
        </>}
    </FleetSection>
  )
}

// Where the error's newest samples came from: the top sources (an app with
// its version and platform, a job, start-up, or a request the log no
// longer keeps), each with its share of the samples read, then the rest.
// Content reads at most `limit` of the newest samples.
const FromBreakdown = ({ data, error, failure }) => {
  const items = (data && data.items) || []
  const total = (data && data.samples) || 0
  const caption = data && total > 0 &&
    `${data.truncated ? `The newest ${plural(total, 'sample')}` : `All ${plural(total, 'kept sample')}`}${data.since ? `, since ${formatDateTime(data.since)}` : ''}.`
  const lines = data && data.other > 0 ? [...items, { kind: 'other', display: `Other (${plural(data.otherLines, 'more source')})`, count: data.other }] : items
  return (
    <FleetSection id="er-from" title="Where it came from">
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-1" />}
      {!data && !error && <p className="sw-hint m-0">Loading…</p>}
      {data && total === 0 && <p className="sw-hint m-0">No samples are kept for this error any more.</p>}
      {total > 0 &&
        <>
          <p className="sw-hint er-section-note">{caption} Each sample's app is read from its request in the Request log.</p>
          <ol className="er-rows er-from" aria-label="Where it came from, most first">
            {lines.map(l => {
              const share = sharePercent(l.count, total)
              return (
                <li key={`${l.kind}:${l.display}`} className={`er-row er-from-row${l.kind === 'unknown' || l.kind === 'other' ? ' er-from-quiet' : ''}`}>
                  <span className="er-from-name" title={l.display}>{l.display}</span>
                  <span className="er-from-count"><b>{formatCount(l.count)}</b> <span className="sw-hint">· {share}</span></span>
                  <span className="er-from-bar" aria-hidden="true"><i style={{ width: `${Math.max(1, Math.round((l.count / total) * 100))}%` }} /></span>
                </li>
              )
            })}
          </ol>
        </>}
    </FleetSection>
  )
}

// The alerts this error raised, newest first: the same dense list, the
// newest few and then the rest in place; past the 20 read here, the alert
// runs list filtered to this error.
const Alerts = ({ id, alerts }) => {
  const [all, setAll] = useState(false)
  const routes = <Link className="btn btn-sm sw-btn" to={ROUTES}>Error alert routes</Link>
  const label = a => a.triggerLabel || a.trigger
  const items = alerts || []
  const shown = all ? items : items.slice(0, FIRST)
  const every = items.length >= 20 &&
    <Link className="btn btn-link btn-sm er-quiet" to={`/admin/alert-routing/runs?subjectRef=${encodeURIComponent(`error_group:${id}`)}`}>Every alert for this error</Link>
  return (
    <FleetSection id="er-alerts" title="Alerts" actions={routes}>
      {alerts === undefined && <p className="sw-hint m-0">Loading…</p>}
      {alerts === null && <p className="text-danger small m-0" role="alert">The alerts couldn't be loaded.</p>}
      {alerts && alerts.length === 0 && <p className="sw-hint m-0">No alerts have been raised for this error.</p>}
      {shown.length > 0 &&
        <ol className="er-rows" aria-label="Alerts, newest first">{shown.map(a =>
          <li key={a.id} className="er-row">
            <div className="er-row-main">
              <Link className="er-row-title" to={`/admin/alert-routing/runs/${a.id}`}>{label(a)}</Link>
              <WordPill words={ALERT_STATUS} value={a.status} />
            </div>
            <span className="er-row-time sw-hint" title={formatDateTime(a.raisedAt)}>{formatAgo(a.raisedAt)}</span>
          </li>)}</ol>}
      {alerts && <ListFoot total={items.length} all={all} onAll={() => setAll(x => !x)} extra={all && every} />}
    </FleetSection>
  )
}

// One error group: what it is, triage (SYS_ERRORS update), activity, links,
// a note, its samples and (SYS_ALERT_ROUTING read) the alerts it raised.
// onLoaded(group): the route names its breadcrumb after the error.
export const ErrorDetail = ({ onLoaded }) => {
  const { groupID } = useParams()
  const id = Number(groupID)
  const { canTriage, readOnly, canOpenRequest, canSeeAlerts } = useErrorRoles()
  const [g, setG] = useState(null)
  const [rangeKey, setRangeKey] = useState(null)
  const [buckets, setBuckets] = useState(null)
  const [bucketsError, bucketsFailure, setBucketsError] = useErrorLine()
  const [from, setFrom] = useState(null)
  const [fromError, fromFailure, setFromError] = useErrorLine()
  const [occ, setOcc] = useState({ items: null, next: null })
  const [occAll, setOccAll] = useState(false)
  const [stacks, setStacks] = useState({})
  const [exporting, setExporting] = useState(false)
  const [alerts, setAlerts] = useState(undefined)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [actionError, actionFailure, setActionError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState({ url: '', label: '' })
  const [linkError, linkFailure, setLinkError] = useErrorLine()
  const [linkForm, setLinkForm] = useState(false)
  const [note, setNoteText] = useState('')
  const [noteSaved, setNoteSaved] = useState(false)
  // "now" for the chart's window, fixed per load so the axis doesn't drift.
  const [now, setNow] = useState(() => Date.now())
  const loaded = useRef(onLoaded)
  loaded.current = onLoaded

  useEffect(() => {
    let live = true
    setG(null)
    setLoadError(null)
    setRangeKey(null)
    getGroup(id).then(d => {
      if (!live) return
      setG(d)
      setNoteText((d && d.note) || '')
      if (d) setRangeKey(pickRange(d))
      if (d && loaded.current) loaded.current(d)
    }, err => live && setLoadError(serverMessage(err, "This error couldn't be loaded."), err))
    if (canSeeAlerts) listGroupAlerts(id).then(r => live && setAlerts((r && r.items) || []), () => live && setAlerts(null))
    return () => { live = false }
  }, [id, canSeeAlerts, setLoadError])

  // The chart's window: the smallest that holds the error's life (set on
  // load), until someone picks another.
  const range = rangeKey
  useEffect(() => {
    if (!range) return undefined
    const r = RANGES.find(x => x.key === range)
    let live = true
    setBuckets(null)
    setBucketsError(null)
    setNow(Date.now())
    getBuckets(id, { days: r.days, step: r.step }).then(
      b => live && setBuckets(Array.isArray(b) ? b : []),
      err => live && setBucketsError(serverMessage(err, "The chart couldn't be loaded."), err))
    return () => { live = false }
  }, [id, range, setBucketsError])

  // Where the samples came from, counted.
  useEffect(() => {
    let live = true
    setFrom(null)
    setFromError(null)
    getBreakdown(id).then(b => live && setFrom(b || { items: [], samples: 0 }),
      err => live && setFromError(serverMessage(err, "Where it came from couldn't be loaded."), err))
    return () => { live = false }
  }, [id, setFromError])

  // The newest page of samples (the list shows its first few).
  useEffect(() => {
    let live = true
    setOcc({ items: null, next: null })
    setOccAll(false)
    setStacks({})
    getOccurrences(id, { limit: PAGE }).then(o => live && setOcc({ items: (o && o.items) || [], next: (o && o.next) || null }),
      err => live && setOcc({ items: [], next: null, error: serverMessage(err, "The samples couldn't be loaded."), failure: err }))
    return () => { live = false }
  }, [id])

  const act = async action => {
    setBusy(true)
    setActionError(null)
    try { setG(await setStatus(id, action)) } catch (err) { setActionError(serverMessage(err, "That didn't work. Try again."), err) } finally { setBusy(false) }
  }
  // Only http(s) links show, and only they are sent back: a bad stored link
  // (the server validates, so this is defence in depth) never blocks a save.
  const stored = (g && g.externalLinks) || []
  const links = stored.filter(l => l && isHttp(l.url))
  const dropped = stored.length - links.length
  const addLink = async () => {
    const url = link.url.trim()
    if (!isHttp(url) || url.length > 2048) { setLinkError('Use an http or https address.'); return }
    if (links.length >= MAX_LINKS) { setLinkError('An error can have at most 10 links.'); return }
    setLinkError(null)
    try {
      // No label: send none (the URL shows instead), so a URL over the
      // 120-character label limit is never refused as a label.
      setG(await setLinks(id, [...links, { url, label: link.label.trim() }]))
      setLink({ url: '', label: '' })
      setLinkForm(false)
    } catch (err) { setLinkError(serverMessage(err, "The link couldn't be saved."), err) }
  }
  const removeLink = async i => {
    setLinkError(null)
    try { setG(await setLinks(id, links.filter((_, j) => j !== i))) } catch (err) { setLinkError(serverMessage(err, "The link couldn't be removed."), err) }
  }
  const saveNote = async () => {
    setActionError(null)
    setNoteSaved(false)
    try { setG(await setNote(id, note)); setNoteSaved(true) } catch (err) { setActionError(serverMessage(err, "The note couldn't be saved."), err) }
  }
  const moreBusy = useRef(false)
  const more = async () => {
    if (moreBusy.current) return
    moreBusy.current = true
    try {
      const o = await getOccurrences(id, { before: occ.next, limit: PAGE })
      setOcc(prev => ({ items: [...(prev.items || []), ...((o && o.items) || [])], next: (o && o.next) || null }))
    } catch (err) { setActionError(serverMessage(err, "Older samples couldn't be loaded."), err) } finally { moreBusy.current = false }
  }

  // The CSV: every kept sample, read page by page (at most 1,000).
  const downloadCsv = async () => {
    setExporting(true)
    setActionError(null)
    try {
      let rows = []
      let before
      do {
        const o = await getOccurrences(id, { before, limit: 100 })
        rows = rows.concat((o && o.items) || [])
        before = (o && o.next) || null
      } while (before && rows.length < 1000)
      exportCsv(rows.map(o => ({ when: o.occurredAt, service: o.service, host: o.host, status: o.status || '', from: (o.from && o.from.display) || '',
        message: occurrenceText(o), request: o.requestID || '', jobRun: o.jobRunID || '' })), `error-${id}-occurrences.csv`)
    } catch (err) { setActionError(serverMessage(err, "The samples couldn't be downloaded."), err) } finally { setExporting(false) }
  }

  if (loadError) return <div className="sw-workspace sw-pane sw-fleet"><Notice tone="bad" role="alert">{loadError}<ErrorReference error={loadFailure} /></Notice></div>
  if (!g) return <div className="sw-workspace sw-pane sw-fleet"><p className="sw-hint">Loading…</p></div>

  const changed = statusLine(g)
  // the title reads from the message for a generic type ("error") and for a
  // browser crash ("TypeError: ..."), so the type shows in Type below
  const titledByMessage = isGenericType(g.errorType) || g.source === 'client'
  const title =
    <span className="er-title">
      <span className="er-title-name">{groupTitle(g)}</span>
      <Pill tone={STATUS_TONE[g.status]} title={changed || undefined}>{STATUS_LABEL[g.status] || g.status}</Pill>
    </span>
  const purpose =
    <span className="er-meta">
      <span>{g.service}</span>
      <span aria-hidden="true">·</span>
      {whereValue(g)}
      {changed && <span className="er-meta-status">{changed}</span>}
    </span>
  const actions =
    <div className="er-head-actions">
      <TriageCopy g={g} latest={(occ.items && occ.items[0]) || null} />
      {canTriage &&
        <div className="er-actions" role="group" aria-label="Status">
          {g.status !== 'resolved' && <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={() => act('resolve')}>Resolve</button>}
          {g.status !== 'ignored' && <button type="button" className="btn sw-btn" disabled={busy} onClick={() => act('ignore')}>Ignore</button>}
          {g.status !== 'open' && <button type="button" className="btn sw-btn" disabled={busy} onClick={() => act('reopen')}>Reopen</button>}
        </div>}
    </div>

  const tiles = [
    { key: 'events', label: 'Events', value: formatCount(g.totalCount || 0), detail: `${formatCount(g.last24h || 0)} in the last 24 hours` },
    { key: 'people', label: 'People affected', value: plural(g.peopleCount || 0, 'person', 'people') },
    { key: 'first', label: 'First seen', value: formatAgo(g.firstSeenAt), detail: formatDateTime(g.firstSeenAt) },
    { key: 'last', label: 'Last seen', value: formatAgo(g.lastSeenAt), detail: formatDateTime(g.lastSeenAt) },
    { key: 'recurred', label: 'Recurred after resolve', value: recurredText(g.cameBackCount), tone: g.cameBackCount ? 'warn' : undefined,
      detail: g.cameBackCount ? `Reopened itself each time · last ${formatAgo(g.cameBackAt)}` : undefined }
  ]

  return (
    <FleetPage title={title} purpose={purpose} actions={actions}>
      {readOnly && <Notice tone="warn" role="status">{READ_ONLY}</Notice>}
      {actionError && <Notice tone="bad" role="alert">{actionError}<ErrorReference error={actionFailure} /></Notice>}
      <RollupTiles tiles={tiles} label="This error at a glance" />

      <FleetSection id="er-what" title="What happened">
        <pre className="er-code er-message" aria-label="Error message">{g.sampleMessage}</pre>
        {/* the service and where it happened are the title's metadata */}
        <KV rows={[
          // the title reads from the message for a generic type ("error") or a
          // browser crash, so the type itself becomes quiet metadata here
          // instead of repeating the title for every type that already names
          // itself
          ['Type', titledByMessage && <span className="sw-mono">{g.errorType}</span>],
          ['Function', g.location && <MiddlePath value={g.location} />],
          ['File', g.locationLine && <MiddlePath value={g.locationLine} />],
          ['Latest request', g.latestRequestID && (canOpenRequest
            ? <Link to={`/admin/request-log/${g.latestRequestID}?at=${encodeURIComponent(g.latestOccurredAt || '')}`}>Open the latest request</Link>
            : <span className="sw-mono">{g.latestRequestID.slice(0, 8)}</span>)]
        ]} />
      </FleetSection>

      <Activity g={g} rangeKey={range} onRange={setRangeKey} buckets={buckets} bucketsError={bucketsError} bucketsFailure={bucketsFailure} now={now} />

      <FromBreakdown data={from} error={fromError} failure={fromFailure} />

      <div className="sw-columns er-columns">
        <FleetSection id="er-note" title="Notes">
          <label className="sw-label visually-hidden" htmlFor="er-note-text">Note</label>
          <textarea id="er-note-text" className="form-control" rows={3} value={note} readOnly={!canTriage}
            placeholder={canTriage ? "What's known about this error, for whoever looks next" : 'No note.'}
            onChange={e => { setNoteSaved(false); setNoteText(e.target.value) }} />
          <div className="er-note-foot">
            <span className={note.length > MAX_NOTE ? 'text-danger small' : 'sw-hint'}>{note.length.toLocaleString()} / 4,000</span>
            {noteSaved && <span className="sw-tone-ok small" role="status">Note saved.</span>}
            {canTriage && <button type="button" className="btn btn-sm sw-btn-primary" disabled={note.length > MAX_NOTE || note === (g.note || '')} onClick={saveNote}>Save note</button>}
          </div>
        </FleetSection>
        <FleetSection id="er-links" title="Links">
          {dropped > 0 && <Notice tone="warn" role="status">{dropped === 1 ? "One saved link isn't a web address, so it's hidden. It will be dropped the next time links are saved." : `${dropped} saved links aren't web addresses, so they're hidden. They will be dropped the next time links are saved.`}</Notice>}
          {links.length === 0 && <p className="sw-hint">No links yet: issues, runbooks or threads about this error.</p>}
          {links.length > 0 &&
            <ul className="er-links">{links.map((l, i) => <li key={l.url + i}><a href={l.url} target="_blank" rel="noreferrer">{l.label || l.url}</a>
              {canTriage && <button type="button" className="btn btn-link btn-sm sw-link-danger" aria-label={`Remove ${l.label || l.url}`} onClick={() => removeLink(i)}>Remove</button>}</li>)}</ul>}
          {canTriage && !linkForm &&
            <button type="button" className="btn btn-sm sw-btn" onClick={() => setLinkForm(true)}>Add a link</button>}
          {canTriage && linkForm && (
            <div className="er-link-form">
              <div className="sw-field">
                <label className="sw-label" htmlFor="er-link-url">Link address</label>
                <input id="er-link-url" autoFocus className="form-control form-control-sm" type="url" placeholder="https://" value={link.url} onChange={e => setLink({ ...link, url: e.target.value })} />
              </div>
              <div className="sw-field">
                <label className="sw-label" htmlFor="er-link-label">Label</label>
                <input id="er-link-label" className="form-control form-control-sm" maxLength={120} placeholder="Optional; the address shows if empty" value={link.label} onChange={e => setLink({ ...link, label: e.target.value })} />
              </div>
              <div className="er-link-buttons">
                <button type="button" className="btn btn-sm sw-btn-primary" onClick={addLink}>Add link</button>
                <button type="button" className="btn btn-sm sw-btn" onClick={() => { setLinkForm(false); setLinkError(null); setLink({ url: '', label: '' }) }}>Cancel</button>
              </div>
            </div>
          )}
          {linkError && <p className="text-danger small m-0 mt-2" role="alert">{linkError}</p>}
          {linkError && <ErrorReference error={linkFailure} className="mt-1" />}
        </FleetSection>
      </div>

      <FleetSection id="er-occurrences" title="Occurrences"
        actions={occ.items && occ.items.length > 0 && <button type="button" className="btn btn-sm sw-btn" disabled={exporting} onClick={downloadCsv}>{exporting ? 'Preparing…' : 'Download CSV'}</button>}>
        <p className="sw-hint er-section-note">Newest first. Up to 10 samples a minute per server are kept; the counts above are exact.</p>
        {occ.error && <p className="text-danger small m-0" role="alert">{occ.error}</p>}
        {occ.error && <ErrorReference error={occ.failure} className="mt-1" />}
        {occ.items === null && <p className="sw-hint m-0">Loading…</p>}
        {occ.items && occ.items.length === 0 && !occ.error && <p className="sw-hint m-0">No samples are kept for this error any more.</p>}
        {occ.items && occ.items.length > 0 &&
          <ol className="er-rows" data-testid="occurrences" aria-label="Occurrences, newest first">
            {(occAll ? occ.items : occ.items.slice(0, FIRST)).map(o =>
              <OccurrenceRow key={o.id} o={o} g={g} canOpenRequest={canOpenRequest} open={Boolean(stacks[o.id])}
                onToggle={() => setStacks(s => ({ ...s, [o.id]: !s[o.id] }))} />)}
          </ol>}
        {occ.items && <ListFoot total={occ.items.length} all={occAll} onAll={() => setOccAll(x => !x)} more={Boolean(occ.next)} onMore={more} />}
      </FleetSection>
      {canSeeAlerts && <Alerts id={id} alerts={alerts} />}
    </FleetPage>
  )
}
